import { useState, useEffect, useRef } from 'react';
import { adminApi, TenantBranding } from '../../services/adminApi';
import { useAdminAccess } from '../../hooks/useAdminAccess';
import { useTenant } from '../../context/TenantContext';

interface Props {
  tenantId?: string; // For SuperAdmin to edit specific tenant
}

export default function BrandingSettings({ tenantId }: Props) {
  const { isSuperAdmin } = useAdminAccess();
  const { config } = useTenant();
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const effectiveTenantId = tenantId || config?.tenant?.id || 'tlg';

  useEffect(() => {
    loadBranding();
  }, [effectiveTenantId]);

  const loadBranding = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getBranding(isSuperAdmin ? effectiveTenantId : undefined);
      setBranding(data.branding);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load branding');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!branding) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await adminApi.updateBranding(branding, isSuperAdmin ? effectiveTenantId : undefined);
      setSuccess('Branding updated successfully! Refresh the page to see changes.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (type: 'logo' | 'favicon', file: File) => {
    if (!file) return;

    try {
      setUploading(true);
      setError(null);

      // Get presigned upload URL
      const { uploadUrl, publicUrl } = await adminApi.getUploadUrl(
        type,
        file.type,
        isSuperAdmin ? effectiveTenantId : undefined
      );

      // Upload file directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      // Update branding with new URL
      const newBranding = {
        ...branding!,
        [type === 'logo' ? 'logo_url' : 'favicon_url']: publicUrl,
      };
      setBranding(newBranding);

      // Save the updated branding
      await adminApi.updateBranding(
        { [type === 'logo' ? 'logo_url' : 'favicon_url']: publicUrl },
        isSuperAdmin ? effectiveTenantId : undefined
      );

      setSuccess(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!`);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to upload ${type}`);
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (field: keyof TenantBranding, value: string) => {
    if (!branding) return;
    setBranding({ ...branding, [field]: value });
  };

  if (loading) {
    return <div className="admin-loading">Loading branding settings...</div>;
  }

  if (!branding) {
    return <div className="admin-error">Failed to load branding settings</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h2>Branding Settings</h2>
        <p className="admin-page-subtitle">
          Customize the look and feel {tenantId ? `for ${tenantId}` : 'of your portal'}
        </p>
      </div>

      {error && (
        <div className="admin-error">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {success && (
        <div className="admin-success">
          {success}
          <button onClick={() => setSuccess(null)}>Dismiss</button>
        </div>
      )}

      <div className="admin-content-area">
        <div className="branding-section">
          <h3>Company Information</h3>
          <div className="form-group">
            <label>Company Name</label>
            <input
              type="text"
              className="admin-input"
              value={branding.company_name}
              onChange={e => handleChange('company_name', e.target.value)}
            />
          </div>
        </div>

        <div className="branding-section">
          <h3>Logo & Favicon</h3>
          <div className="logo-upload-grid">
            <div className="upload-box">
              <label>Logo</label>
              <div className="preview-area">
                {branding.logo_url ? (
                  <img src={branding.logo_url} alt="Logo" className="logo-preview" />
                ) : (
                  <div className="placeholder">No logo uploaded</div>
                )}
              </div>
              <input
                type="file"
                ref={logoInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => e.target.files?.[0] && handleFileUpload('logo', e.target.files[0])}
              />
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Logo'}
              </button>
            </div>

            <div className="upload-box">
              <label>Favicon</label>
              <div className="preview-area favicon-preview-area">
                {branding.favicon_url ? (
                  <img src={branding.favicon_url} alt="Favicon" className="favicon-preview" />
                ) : (
                  <div className="placeholder">No favicon</div>
                )}
              </div>
              <input
                type="file"
                ref={faviconInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => e.target.files?.[0] && handleFileUpload('favicon', e.target.files[0])}
              />
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => faviconInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Favicon'}
              </button>
            </div>
          </div>
        </div>

        <div className="branding-section">
          <h3>Colors</h3>
          <div className="color-grid">
            <div className="color-input">
              <label>Primary Color</label>
              <div className="color-picker-row">
                <input
                  type="color"
                  value={branding.primary_color}
                  onChange={e => handleChange('primary_color', e.target.value)}
                />
                <input
                  type="text"
                  className="admin-input"
                  value={branding.primary_color}
                  onChange={e => handleChange('primary_color', e.target.value)}
                />
              </div>
            </div>

            <div className="color-input">
              <label>Accent Color</label>
              <div className="color-picker-row">
                <input
                  type="color"
                  value={branding.accent_color}
                  onChange={e => handleChange('accent_color', e.target.value)}
                />
                <input
                  type="text"
                  className="admin-input"
                  value={branding.accent_color}
                  onChange={e => handleChange('accent_color', e.target.value)}
                />
              </div>
            </div>

            <div className="color-input">
              <label>Background Color</label>
              <div className="color-picker-row">
                <input
                  type="color"
                  value={branding.background_color}
                  onChange={e => handleChange('background_color', e.target.value)}
                />
                <input
                  type="text"
                  className="admin-input"
                  value={branding.background_color}
                  onChange={e => handleChange('background_color', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="branding-section">
          <h3>Preview</h3>
          <div
            className="branding-preview"
            style={{
              background: branding.background_color,
              '--preview-accent': branding.primary_color,
            } as React.CSSProperties}
          >
            <div className="preview-header">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="Logo" />
              ) : (
                <span style={{ color: branding.primary_color }}>{branding.company_name}</span>
              )}
            </div>
            <div className="preview-button" style={{ background: branding.primary_color }}>
              Sample Button
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            className="admin-btn admin-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
